import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  IconButton,
  Input,
  InputAdornment,
  Switch,
  Tooltip,
  Checkbox,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  EditOutlined,
} from "@mui/icons-material";
import { debounce } from "lodash";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { vehicleServices, apiCategory } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import ViewVehicle from "./ViewVehicle";
import { Loader } from "../../components/CustomLoader";
import { IMAGE_BASE, STATUS_OPTIONS_LIST } from "../../utils/constants";
import CustomPagination from "../../components/CustomPagination";
import { useBulkSelect } from "../../hooks/useBulkSelect";

export default function VehicleManagementList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [category, setCategory] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fuelType, setFuelType] = useState([]);
  const [auctionType, setAuctionType] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, id: "", currentStatus: 0 });

  const {
    selectedRows,
    getCheckboxColumn,
    clearSelection,
  } = useBulkSelect(data, null, [currentPage, pageSize, status, refresh, category, fuelType, auctionType]);

  const fetchVehicles = async (query = "") => {
    try {
      setIsLoading(true);
      setShowLoader(true);
      
      const body = {
        searchTerm: query,
        pageNumber: currentPage,
        pageSize,
        status,
        categoryId: category,
        fuelType,
      };
      
      const res = await vehicleServices.getAllVehicles(body);
      
      // Show loader for minimum 1 second for better UX
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.vehicles || []);
          setTotalCount(res?.data?.data?.total || res?.data?.data?.totalPages || 0);
        }
        setShowLoader(false);
      }, 1000);
      
    } catch (err) {
      setData([]);
      setShowLoader(false);
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiCategory.getAll({
        pageNumber: 1,
        pageSize: 100, 
        status: [1] 
      });
      if (response?.status === 200) {
        setCategories(response?.data?.data?.categories || []);
      }
    } catch (error) {
      errorHandler(error);
    }
  }, []);

  const debouncedSearch = useCallback(debounce(fetchVehicles, 400), [currentPage, pageSize, status, category, fuelType, auctionType]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const deleteVehicle = async (ids) => {
    const isMultiple = Array.isArray(ids);
    const result = await Swal.fire({
      text: isMultiple 
        ? `Are you sure you want to delete ${ids.length} selected vehicle(s)?`
        : "Are you sure you want to delete this vehicle?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (result.isConfirmed) {
      try {
        const vehicleIds = isMultiple ? ids : [ids];
        const payload = vehicleIds.length === 1 
          ? { id: vehicleIds[0] }
          : { id: vehicleIds };
        
        const res = await vehicleServices.deleteVehicle(payload);
        if (res?.status === 200) {
          toast.success(res.data.message);
          clearSelection();
          setRefresh(!refresh);
        } else {
          toast.error(res.data.message);
        }
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.length > 0) {
      deleteVehicle(selectedRows);
    }
  };

  const toggleStatus = async () => {
    const { id, currentStatus } = statusDialog;
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await vehicleServices.changeStatus({ id, status: newStatus });
      if (res?.status === 200) {
        toast.success(res.data.message);
        setRefresh(!refresh);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setStatusDialog({ open: false, id: "", currentStatus: 0 });
    }
  };

  useEffect(() => {
    fetchVehicles(searchItem);
    fetchCategories();
  }, [currentPage, pageSize, status, refresh, category, fuelType, auctionType, fetchCategories]);

  const columns = [
    getCheckboxColumn(),
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: '100px'
    },
    {
      name: "Image",
      cell: (row) => (
        <Box
          component="img"
          src={row.vehicleImage?.[0] ? IMAGE_BASE + row.vehicleImage[0] : "/assets/placeholder.svg"}
          alt="Vehicle"
          sx={{
            width: 60,
            height: 40,
            objectFit: "cover",
            borderRadius: 1,
          }}
        />
      ),
    },
    {
      name: "Vehicle Name",
      cell: (row) => (
        <Box>
          <Typography variant="subtitle2">
            {row?.make?.name + " " + row?.model}
          </Typography>
        
        </Box>
      ),
      sortable: true,
      width: "200px",
     
    },
    {
      name: "Year",
      selector: (row) => row.year,
      sortable: true,
    },
    {
      name: "VIN",
      cell: (row) => (
        <Box>
          <Typography variant="body3">
            {row?.VIN}
          </Typography>
        
        </Box>
      ),
      sortable: true,
      
    },
    {
      name: "Odometer",
      selector: (row) => `${row.miles || 0} ${row.odometerUnit || "KM"}`,
      sortable: true,
      width: "150px",
     
    },
    {
      name: "Original Price",
      selector: (row) => row.originalPrice ? `$${row.originalPrice.toLocaleString()}` : "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: "Fuel Type",
      selector: (row) => row.fuelType,
      sortable: true,
      width: "150px",
     
    },
    {
      name: "Category",
      selector: (row) => row.category[0]?.name,
      sortable: true,
   
    },
    {
      name: "Status",
      cell: (row) => {
        const isActive = row.status === 1;
        return (
          <Switch
            checked={isActive}
            onClick={() => {
              setStatusDialog({ open: true, id: row._id, currentStatus: isActive ? 1 : 0 });
            }}
            color="primary"
            inputProps={{ "aria-label": "controlled" }}
          />
        );
      },
      width: "100px",
    },
    {
      name: "Actions",
      center: true,
      width: "180px",
      cell: (row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="View Details">
            <IconButton 
              onClick={() => { setModalShow(true); setViewId(row._id); }}
              sx={{ color: "primary.main" }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton 
              onClick={() => navigate(`/vehicle-management/edit/${row._id}`)}
              sx={{ color: "primary.main" }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton 
              onClick={() => deleteVehicle(row._id)}
              sx={{ color: "error.main" }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];


  return (
    <Card>
      <CardHeader title="Vehicle Management" />
      <Box display="flex" flexWrap="wrap" justifyContent="flex-end" alignItems="center" p={2} gap={2}>
        <TableFilter
          data={categories.map(cat => ({
            label: cat.name,
            value: cat._id
          }))}
          value={category}
          setValue={setCategory}
          filterName="Category"
        />
        <TableFilter
          data={[
            { label: "Gas", value: "Gas" },
            { label: "Diesel", value: "Diesel" },
            { label: "Hybrid", value: "HYBRID" },
            { label: "Electric", value: "Electric" },
            { label: "CNG/LPG", value: "LPG/CNG" },
          ]}
          value={fuelType}
          setValue={setFuelType}
          filterName="Fuel Type"
        />
        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />
        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => { setSearchItem(""); fetchVehicles(""); }}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        )}
        <Button variant="contained" onClick={() => navigate("/vehicle-management/add")}>
          Add Vehicle
        </Button>
      </Box>

      <CardContent>
         <DataTable
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalCount}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          onChangeRowsPerPage={(rows) => {
            setPageSize(rows);
            setCurrentPage(1);
          }}
          progressPending={showLoader}
          progressComponent={<Loader />}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationComponent={(props) => (
            <CustomPagination {...props} currentPage={currentPage} />
          )}
          customStyles={{
            headCells: {
              style: { fontWeight: "bold", fontSize: "14px" },
            },
          }}
        />
      </CardContent>

      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, id: "", currentStatus: 0 })}>
        <DialogTitle>
          Are you sure you want to {statusDialog.currentStatus === 1 ? "deactivate" : "activate"} this vehicle?
        </DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={toggleStatus} color="primary">
            Yes
          </Button>
          <Button variant="outlined" onClick={() => setStatusDialog({ open: false, id: "", currentStatus: 0 })}>
            No
          </Button>
        </DialogActions>
      </Dialog>

      {modalShow && (
        <ViewVehicle show={modalShow} onHide={() => setModalShow(false)} id={viewId} />
      )}
    </Card>
  );
}

